import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Minus, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText } from '../../../styling/stylingHelper';
import { useAuth } from '../../../services/authentication/authContext';
import { useSubscription } from '../../../hooks/useSubscription';
import { getAccessibleFaqEntries, SUPPORT_FAQ_TOPICS } from './supportGuideRegistry';

export default function SupportFaqPage() {
  const { t } = useTranslation('supportFaq');
  const { backendUser, accountSettings } = useAuth();
  const { isActive } = useSubscription();
  const [search, setSearch] = useState('');
  const query = search.trim().toLocaleLowerCase();
  const matchingEntries = getAccessibleFaqEntries(backendUser, accountSettings, isActive).filter(entry =>
    `${t(`entries.${entry.id}.question`)} ${t(`entries.${entry.id}.answer`)}`.toLocaleLowerCase().includes(query));
  const topics = SUPPORT_FAQ_TOPICS.filter(topic => matchingEntries.some(entry => entry.topic === topic));
  const entries = topics.flatMap(topic => matchingEntries.filter(entry => entry.topic === topic));

  return <main className="mx-auto w-full max-w-7xl p-4 pb-24 sm:p-8 sm:pb-24 lg:pb-8 text-text dark:text-text-dark">
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-12">
      <aside className="min-w-0 lg:sticky lg:top-8">
        <header className="flex items-center gap-2">
          <Link to="/support" aria-label={t('supportGuides:_ui.back')} className={cn('flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center', primaryText)}><ArrowLeft size={24} aria-hidden="true" /></Link>
          <h1 className="text-2xl sm:text-3xl font-bold">{t('title')}</h1>
        </header>
        <p className="mt-3 leading-relaxed text-muted dark:text-muted-dark">{t('subtitle')}</p>
        <label className="mt-6 block"><span className="sr-only">{t('search')}</span><input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('search')} className="w-full rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark p-3 text-text dark:text-text-dark placeholder:text-muted dark:placeholder:text-muted-dark" /></label>
        {topics.length > 0 && <nav aria-label={t('jumpTo')} className="mt-6 lg:mt-10">
          <p className="mb-3 text-sm text-muted dark:text-muted-dark">{t('jumpTo')}</p>
          <div className="flex gap-4 overflow-x-auto border-l-2 border-textGreen dark:border-textGreen-dark pl-3 lg:flex-col lg:gap-0">
            {topics.map(topic => <a key={topic} href={`#${entries.find(entry => entry.topic === topic)!.id}`}
              className="inline-flex min-h-[44px] shrink-0 items-center text-sm hover:text-textGreen dark:hover:text-textGreen-dark sm:text-base">{t(`topics.${topic}`)}</a>)}
          </div>
        </nav>}
      </aside>
      <div className="min-w-0 space-y-3">
        {!entries.length && <p role="status" className="text-muted dark:text-muted-dark">{t('empty')}</p>}
        {entries.map((entry, index) => <details key={entry.id} id={entry.id} className="group scroll-mt-24 rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark">
          <summary className="flex min-h-[56px] cursor-pointer list-none items-center gap-3 px-4 py-2 [&::-webkit-details-marker]:hidden sm:gap-4 sm:px-5">
            <span className="shrink-0 tabular-nums text-textGreen dark:text-textGreen-dark" aria-hidden="true">{String(index + 1).padStart(3, '0')}</span>
            <span className="min-w-0 flex-1 break-words font-medium">{t(`entries.${entry.id}.question`)}</span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border dark:border-border-dark text-muted dark:text-muted-dark">
              <Plus size={16} className="group-open:hidden" aria-hidden="true" />
              <Minus size={16} className="hidden group-open:block" aria-hidden="true" />
            </span>
          </summary>
          <div className="px-4 pb-4 sm:px-5">
            <p className="pt-1 leading-relaxed text-muted dark:text-muted-dark">{t(`entries.${entry.id}.answer`)}</p>
            <Link to={`/support/guides/${entry.guide.id}#${entry.section}`} className="mt-2 inline-flex min-h-[44px] items-center gap-2 text-textGreen dark:text-textGreen-dark">{t('guide')}<ArrowRight size={16} aria-hidden="true" /></Link>
          </div>
        </details>)}
      </div>
    </div>
  </main>;
}
